import { supabase } from "@/integrations/supabase/client";
import { functionUrl } from "@/utils/supabaseUrls";

export type SignedVideoResponse = {
  url: string;
};

/**
 * Fetches a short‑lived signed URL for a private video in Supabase Storage.
 * The Edge Function must be deployed as `get-signed-video`.
 *
 * Params:
 *  - filePath: path inside the bucket, e.g. "WS00003/video.mp4"
 *  - bucket: optional bucket name (defaults to "admin")
 *  - ttlSeconds: optional TTL in seconds (defaults to 3600)
 */
type SignedVideoOptions = {
  bucket?: string;
  ttlSeconds?: number;
  suppressErrorLog?: boolean;
};

export async function getSignedVideoUrl(
  filePath: string,
  options?: SignedVideoOptions
): Promise<string> {
  if (!filePath || typeof filePath !== "string")
    throw new Error("getSignedVideoUrl: filePath is required");

  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session) throw new Error("Not authenticated");

  const params = new URLSearchParams();
  params.set("filePath", filePath);
  if (options?.bucket) params.set("bucket", options.bucket);
  if (options?.ttlSeconds) params.set("ttl", String(options.ttlSeconds));

  const url = `${functionUrl("get-signed-video")}?${params.toString()}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (!res.ok) {
    let msg = `getSignedVideoUrl failed: HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg += ` - ${body.error}`;
      if (!options?.suppressErrorLog) {
        console.error("❌ getSignedVideoUrl: Error response:", body);
      }
    } catch (e) {
      if (!options?.suppressErrorLog) {
        console.error(
          "❌ getSignedVideoUrl: Could not parse error response:",
          e
        );
      }
    }
    if (!options?.suppressErrorLog) {
      console.error("❌ getSignedVideoUrl: Full error:", msg);
    }
    throw new Error(msg);
  }

  const data = (await res.json()) as SignedVideoResponse;
  if (!data?.url || typeof data.url !== "string")
    throw new Error("getSignedVideoUrl: invalid response");
  return data.url;
}
